import React from "react";
import { Button, Space, Tooltip } from "antd";
import { ButtonProps } from "antd/lib/button";
import Icon, { CheckCircleFilled, ExclamationCircleFilled, EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { filter, isEmpty, map } from "lodash";
import classNames from "classnames";
import * as Icons from "assets/icons";
import CustomDot from "components/CustomDot";
import {
  AICC_AUDIENCE,
  AICC_LANGUAGE_ACTION,
  AICC_LANGUAGE_ACTION_LABEL,
  AICC_LANGUAGE_STATUS_COLOR,
  AICC_LANGUAGE_STATUS_LABEL,
  AICC_READINESS_BLOCKED_COLOR,
  AICC_STATUS_DOT_SIZE,
  AICC_VERSION_ROW,
  AiccLanguageAction,
} from "constants/aiccLanguage";
import { AiccManagedLanguage } from "types/model/aiccLanguage";
import { AiccAssetReadiness, AiccLanguageVersionRow as VersionRow } from "../languageStatus";
import { DUPLICATE_PREVIEW_HINT } from "../configs";
import styles from "./VersionRows.module.scss";

/**
 * Filled for the two actions that change what end users can see, outlined for the one that only
 * opens editing, and a quiet link for staging.
 */
const ACTION_BUTTON_PROPS: Partial<Record<AiccLanguageAction, ButtonProps>> = {
  [AICC_LANGUAGE_ACTION.readyToPublish]: { type: "link", className: styles.linkButton },
  [AICC_LANGUAGE_ACTION.publicToAllUser]: { type: "primary", shape: "round", size: "small" },
  [AICC_LANGUAGE_ACTION.duplicatePreview]: { shape: "round", size: "small" },
};

/** Only for actions whose label does not give away what they do. */
const ACTION_HINT: Partial<Record<AiccLanguageAction, string>> = {
  [AICC_LANGUAGE_ACTION.duplicatePreview]: DUPLICATE_PREVIEW_HINT,
};

/** How much of this copy is done. Frozen versions have nothing outstanding, so they get none. */
function ReadinessChip({ readiness: { total, ready, blocked } }: { readiness: AiccAssetReadiness }) {
  if (blocked) {
    return (
      <span className={styles.readinessBlocked} style={{ color: AICC_READINESS_BLOCKED_COLOR }}>
        <ExclamationCircleFilled />
        {`${ready}/${total} assets ready`}
      </span>
    );
  }

  return (
    <span className={styles.readinessReady}>
      <CheckCircleFilled />
      {`All ${total} assets ready`}
    </span>
  );
}

interface Props {
  language: AiccManagedLanguage;
  row: VersionRow;
  /**
   * Open state is owned by the list rather than the row, because one row can be opened from outside:
   * the unpublish dialog's "Go to Edit V2" points at the working copy (AICC2-331 AC-5).
   */
  isOpen: boolean;
  onToggle: () => void;
  /** The language is being re-read, so its actions would act on data about to be replaced. */
  isBusy?: boolean;

  onAction?: Partial<Record<AiccLanguageAction, (language: AiccManagedLanguage) => void>>;
  /** The sub-asset table for this version, rendered only while the row is open. */
  children: React.ReactNode;
}

/**
 * One version of a language: its number, state, audience and the actions that belong to it.
 *
 * Every row expands, including the frozen ones — a published version's sub-assets are worth
 * reading even though nothing there can be edited. Rows are peers on a timeline rather than one
 * nested inside another: a copy indented under a release read as that release's expanded
 * content, which is what appears in the same place when it is opened.
 */
function LanguageVersionRow({ language, row, isOpen, onToggle, isBusy, onAction, children }: Props) {
  const actions = filter(row.actions, (action) => !!onAction?.[action]);

  return (
    <div className={styles.versionRow}>
      <div className={styles.versionRowHeader} onClick={onToggle}>
        {/* Filled once the release exists; a working copy has not happened yet. */}
        <span
          className={classNames(styles.versionRowNode, {
            [styles.versionRowNodeReleased]: row.kind !== AICC_VERSION_ROW.working,
          })}
        />

        <Icon component={Icons.CareRight} className={styles.versionRowArrow} rotate={isOpen ? 90 : 0} />

        {/*
          Number and origin are one unit: the parentheses and the tighter gap say the provenance
          belongs to this version rather than being another fact on the row.
        */}
        <span className={styles.versionRowVersion}>
          <span className={styles.versionRowNumber}>{row.versionLabel}</span>
          {!!row.origin && <span className={styles.versionRowOrigin}>{`(${row.origin})`}</span>}
        </span>

        <CustomDot color={AICC_LANGUAGE_STATUS_COLOR[row.status]} size={AICC_STATUS_DOT_SIZE}>
          {AICC_LANGUAGE_STATUS_LABEL[row.status]}
        </CustomDot>

        {!!row.readiness?.total && <ReadinessChip readiness={row.readiness} />}

        <span className={styles.versionRowAudience}>
          {/*
            The icon answers whether anyone can reach this version at all; which audience it is
            belongs to the words beside it, so a crossed-out eye is reserved for nobody.
          */}
          {row.audience === AICC_AUDIENCE.nobody ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          {row.audience}
        </span>

        {!isEmpty(actions) && (
          // The row toggles on click, so the actions keep their clicks to themselves.
          <Space size={12} className={styles.versionRowActions} onClick={(event) => event.stopPropagation()}>
            {map(actions, (action) => {
              const handler = onAction?.[action];
              const hint = ACTION_HINT[action];

              const button = (
                <Button disabled={isBusy} onClick={() => handler?.(language)} {...ACTION_BUTTON_PROPS[action]}>
                  {AICC_LANGUAGE_ACTION_LABEL[action]}
                </Button>
              );

              // A disabled button fires no events, so the tooltip needs a wrapper to hang off.
              return hint ? (
                <Tooltip key={action} title={hint}>
                  <span>{button}</span>
                </Tooltip>
              ) : (
                <React.Fragment key={action}>{button}</React.Fragment>
              );
            })}
          </Space>
        )}
      </div>

      {isOpen && <div className={styles.versionRowBody}>{children}</div>}
    </div>
  );
}

export default LanguageVersionRow;
